import React, { useEffect, useMemo, useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';

function calculateMonthsSince(dateString) {
  if (!dateString) return null;
  const start = new Date(dateString);
  if (Number.isNaN(start.getTime())) return null;
  const now = new Date();
  const diffMs = now.getTime() - start.getTime();
  if (diffMs <= 0) return 0;
  const days = diffMs / (1000 * 60 * 60 * 24);
  const months = days / 30;
  return Math.round(months * 10) / 10;
}

export default function PathwayVerificationEligibleBatchesNotifications() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      setLoading(true);
      setError('');
      try {
        const response = await axiosInstance.get('/admin/registrations/pathway-verification-eligible');
        if (!cancelled) {
          const payload = response.data || {};
          setStudents(Array.isArray(payload.students) ? payload.students : []);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load pathway verification eligible students', err);
          setError('Failed to load pathway verification eligible students. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadData();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredStudents = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return students;
    }
    return students.filter((student) => {
      const haystack = [
        student.studentName,
        student.studentId,
        student.nicNumber,
        student.registrationNumber,
        student.courseName,
        student.courseShortName,
        student.batchName,
        student.batchInternalName,
        student.whatsappGroupName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [students, search]);

  const totalCount = students.length;
  const visibleCount = filteredStudents.length;

  const handleDownloadExcel = async () => {
    if (!filteredStudents.length) {
      return;
    }

    try {
      const XLSX = await import('xlsx');

      const headers = [
        'Student ID',
        'Student Name',
        'NIC',
        'DOB',
        'Telephone',
        'Email',
        'Address',
        'Registration Number',
        'Registration Status',
        'Delivery Status',
        'University Register Date',
        'Course Name',
        'Course Short Name',
        'Batch Name',
        'Batch Internal Name',
        'Batch Starting Date',
        'WhatsApp Group',
        'Months Since Batch Start',
      ];

      const rows = filteredStudents.map((student) => {
        const months = calculateMonthsSince(student.batchStartingDate);
        return [
          student.studentId || '',
          student.studentName || '',
          student.nicNumber || '',
          student.dob || '',
          student.telephone || '',
          student.email || '',
          student.address || '',
          student.registrationNumber || '',
          student.registrationStatus || '',
          student.deliveryStatus || '',
          student.universityRegisterDate || '',
          student.courseName || '',
          student.courseShortName || '',
          student.batchName || '',
          student.batchInternalName || '',
          student.batchStartingDate || '',
          student.whatsappGroupName || '',
          months == null ? '' : months,
        ];
      });

      const worksheetData = [headers, ...rows];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Pathway Eligible');

      const today = new Date().toISOString().split('T')[0];
      const fileName = `Pathway_Verification_Eligible_${today}.xlsx`;

      XLSX.writeFile(workbook, fileName);
    } catch (err) {
      console.error('Failed to download pathway eligible Excel', err);
    }
  };

  return (
    <DashboardLayout title="Batches Eligible for Pathway Verification">
      <div className="space-y-6">
        <div className="space-y-1">
          <h1 className="text-xl font-semibold text-gray-900">Batches Eligible for Pathway Verification</h1>
          <p className="text-sm text-gray-600">
            This view lists students in Level 5 Pearson BTEC HND in Computing batches where the batch has been running for at
            least 2.5 months but the registration stream is still not assigned.
          </p>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 border border-red-200">{error}</div>
        )}

        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 border border-blue-100">
              Total students pending pathway selection: <span className="ml-1 font-semibold">{totalCount}</span>
            </span>
            {totalCount > 0 && (
              <span className="text-xs text-gray-500">Showing {visibleCount} of {totalCount}</span>
            )}
          </div>
          <div className="flex w-full max-w-md flex-col gap-2 sm:flex-row sm:items-end sm:justify-end">
            <div className="w-full sm:w-64">
              <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="pathway-search">
                Search by student, registration, batch or course
              </label>
              <input
                id="pathway-search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Type to search..."
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            {visibleCount > 0 && (
              <button
                type="button"
                onClick={handleDownloadExcel}
                className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-xs font-medium text-white shadow-sm transition-colors hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download as Excel
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-gray-600">Loading pathway verification eligible students...</div>
        ) : visibleCount === 0 ? (
          <div className="rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
            No students currently meet the pathway verification criteria for Level 5 Pearson BTEC HND in Computing batches.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Student</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Contact</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Registration</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Batch & Course</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Months since start</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Stream Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {filteredStudents.map((student) => (
                  <tr key={student.registrationId} className="hover:bg-gray-50">
                    <td className="px-4 py-2 align-top">
                      <div className="font-medium text-gray-900">{student.studentName || 'N/A'}</div>
                      <div className="text-xs text-gray-500">
                        ID: {student.studentId || 'N/A'}
                      </div>
                      {student.nicNumber && (
                        <div className="text-xs text-gray-500">NIC: {student.nicNumber}</div>
                      )}
                      {student.dob && (
                        <div className="text-xs text-gray-500">DOB: {student.dob}</div>
                      )}
                    </td>
                    <td className="px-4 py-2 align-top text-xs text-gray-700">
                      {student.telephone && <div>Tel: {student.telephone}</div>}
                      {student.email && <div className="truncate">Email: {student.email}</div>}
                      {student.address && <div className="mt-1 line-clamp-2">{student.address}</div>}
                    </td>
                    <td className="px-4 py-2 align-top text-xs text-gray-700">
                      <div className="font-medium text-gray-900">{student.registrationNumber}</div>
                      <div className="mt-1 inline-flex items-center rounded-full bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-700 border border-slate-200">
                        {student.registrationStatus}
                      </div>
                      <div className="mt-1 text-[11px] text-gray-500">
                        Delivery status: {student.deliveryStatus || 'Not set'}
                      </div>
                      {student.universityRegisterDate && (
                        <div className="mt-1 text-[11px] text-gray-500">
                          University registered: {student.universityRegisterDate}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 align-top text-xs text-gray-700">
                      <div className="font-medium text-gray-900">
                        {student.courseShortName || student.courseName || 'N/A'}
                      </div>
                      <div className="text-[11px] text-gray-500">{student.courseName}</div>
                      <div className="mt-1 text-[11px] text-gray-700">
                        Batch: {student.batchName || student.batchInternalName || 'N/A'}
                      </div>
                      {student.batchInternalName && (
                        <div className="text-[11px] text-gray-500">Internal: {student.batchInternalName}</div>
                      )}
                      {student.batchStartingDate && (
                        <div className="text-[11px] text-gray-500">Started: {student.batchStartingDate}</div>
                      )}
                      {student.whatsappGroupName && (
                        <div className="text-[11px] text-gray-500">WhatsApp: {student.whatsappGroupName}</div>
                      )}
                    </td>
                    <td className="px-4 py-2 align-top text-xs text-gray-700">
                      {(() => {
                        const months = calculateMonthsSince(student.batchStartingDate);
                        if (months === null) return '-';
                        return `${months.toString()} month${months === 1 ? '' : 's'}`;
                      })()}
                    </td>
                    <td className="px-4 py-2 align-top">
                      <span className="inline-flex items-center rounded-full bg-amber-50 px-3 py-1 text-[11px] font-medium text-amber-800 border border-amber-200">
                        Stream not assigned
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
